import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import AppShell from "../components/AppShell";
import {
  UsersThree, UserPlus, Crown, Trash, ShieldCheck, Pencil,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const ACCESS_LABEL = {
  full: "Full access",
  editor: "Editor",
  viewer: "Viewer",
};

const ACCESS_DESCRIPTION = {
  full: "Can create projects, add items, and invite teammates.",
  editor: "Can add/remove items but can't create projects or invite teammates.",
  viewer: "Read-only access to projects and items.",
};

function formatApiError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || d).join(", ");
  return null;
}

export default function TeamPage() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  const canManage = user?.role === "contractor" && (user?.team_access || "full") === "full";
  const isContractor = user?.role === "contractor";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, i] = await Promise.all([
        api.get("/team/contractor-team"),
        canManage ? api.get("/team/contractor-invites") : Promise.resolve({ data: [] }),
      ]);
      setMembers(m.data);
      setInvites(i.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load team");
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    if (isContractor) load();
    else setLoading(false);
  }, [isContractor, load]);

  if (!isContractor) {
    return (
      <AppShell>
        <div className="space-y-6">
          <div className="ctrl-card p-10 text-center">
            <p className="label-eyebrow text-zinc-400">Team management</p>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#001E44] mt-1">
              Available to contractor accounts
            </h1>
            <p className="text-sm text-zinc-500 mt-3 max-w-md mx-auto">
              Sign in as a contractor to invite teammates from your company. As an
              admin or project manager you can already invite users from the Users page.
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
    <div className="space-y-8" data-testid="team-page">
      {/* Hero strip with animated stat counters */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#001E44] via-[#0A2E5C] to-[#1E407C] text-white p-8 sm:p-10">
        <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
          backgroundImage: "radial-gradient(circle at 80% 20%, rgba(0,123,255,0.35), transparent 50%), radial-gradient(circle at 20% 80%, rgba(16,185,129,0.25), transparent 50%)",
        }} />
        <div className="relative flex flex-col lg:flex-row gap-6 lg:items-end justify-between">
          <div className="max-w-xl">
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-200 font-semibold flex items-center gap-2">
              <UsersThree size={12} weight="bold" /> Your company team
            </p>
            <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-2 leading-[1.05]">
              Build your crew.
            </h1>
            <p className="text-sm text-blue-100/90 mt-3 leading-relaxed">
              Invite colleagues from your company so they can collaborate on the
              projects you run. Pick their job title and dial in their access — Full,
              Editor or Viewer.
            </p>
          </div>
          {canManage && (
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  className="bg-white hover:bg-blue-50 text-[#001E44] rounded-full font-semibold px-6 py-2.5 h-auto shadow-lg shrink-0 transition-all hover:shadow-xl hover:scale-[1.02]"
                  data-testid="invite-teammate-button"
                >
                  <UserPlus size={14} weight="bold" className="mr-1.5" />
                  Invite team member
                </Button>
              </DialogTrigger>
              <InviteTeammateDialog onInvited={load} />
            </Dialog>
          )}
        </div>
        {/* Stats strip */}
        <div className="relative grid grid-cols-2 sm:grid-cols-4 gap-3 mt-8 pt-6 border-t border-white/15">
          {[
            { label: "Total members", value: members.length, accent: "text-white" },
            { label: "Full access", value: members.filter((m) => m.team_access === "full").length, accent: "text-blue-200" },
            { label: "Editors", value: members.filter((m) => m.team_access === "editor").length, accent: "text-emerald-200" },
            { label: "Pending invites", value: invites.length, accent: "text-amber-200" },
          ].map((s) => (
            <div key={s.label} data-testid={`team-stat-${s.label.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className={`font-display text-3xl sm:text-4xl font-bold tracking-tight ${s.accent}`}>
                {s.value}
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-blue-100/70 font-semibold mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Members */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="font-display text-2xl font-semibold tracking-tight text-[#001E44]">
            Active members
          </h2>
          <span className="text-xs text-zinc-400">{members.length} total</span>
        </div>
        {loading ? (
          <div className="ctrl-card p-10 text-center text-sm text-zinc-400">Loading…</div>
        ) : members.length === 0 ? (
          <div className="ctrl-card p-10 text-center text-sm text-zinc-400">No teammates yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="team-members-list">
            {members.map((m) => {
              const accessBg = m.team_access === "full"
                ? "from-[#007BFF]/8 to-transparent"
                : m.team_access === "editor"
                ? "from-emerald-100/40 to-transparent"
                : "from-zinc-100/60 to-transparent";
              return (
                <div
                  key={m.id}
                  className={`group relative ctrl-card p-5 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200`}
                  data-testid={`team-member-${m.id}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${accessBg} opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
                  <div className="relative flex items-start gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#001E44] to-[#1E407C] text-white rounded-2xl flex items-center justify-center text-base font-display font-bold shrink-0 shadow-sm group-hover:scale-105 transition-transform">
                      {m.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-semibold text-[#001E44] leading-tight flex items-center gap-1.5 truncate">
                        {m.name}
                        {m.is_team_owner && (
                          <Crown size={12} weight="fill" className="text-[#007BFF] shrink-0" />
                        )}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5 truncate">
                        {m.job_title || (m.is_team_owner ? "Team owner" : "Teammate")}
                      </div>
                    </div>
                    {canManage && !m.is_team_owner && (
                      <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Dialog>
                          <DialogTrigger asChild>
                            <button
                              className="p-1.5 rounded text-zinc-400 hover:bg-zinc-100 hover:text-[#001E44] transition-colors"
                              title="Edit teammate"
                              data-testid={`edit-team-member-${m.id}`}
                            >
                              <Pencil size={14} weight="bold" />
                            </button>
                          </DialogTrigger>
                          <EditTeammateDialog member={m} onSaved={load} />
                        </Dialog>
                        <RemoveTeammateButton member={m} onRemoved={load} />
                      </div>
                    )}
                  </div>
                  <div className="relative mt-4 pt-4 border-t border-zinc-100 flex items-center justify-between gap-2">
                    <span className={`chip text-[10px] ${
                      m.team_access === "full" ? "bg-[#007BFF]/10 text-[#007BFF]"
                      : m.team_access === "editor" ? "bg-emerald-50 text-emerald-700"
                      : "bg-zinc-100 text-zinc-600"
                    }`}>
                      <ShieldCheck size={10} weight="bold" className="mr-1" />
                      {ACCESS_LABEL[m.team_access] || m.team_access}
                    </span>
                    <a
                      href={`mailto:${m.email}`}
                      className="text-[10px] uppercase tracking-[0.15em] font-semibold text-zinc-400 hover:text-[#007BFF] transition-colors truncate"
                      title={m.email}
                    >
                      {m.email}
                    </a>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pending invites */}
      {canManage && (
        <div className="ctrl-card">
          <div className="px-5 py-4 border-b border-zinc-100 flex items-baseline justify-between">
            <h2 className="font-display font-bold tracking-tight text-[#001E44]">
              Pending invites
            </h2>
            <span className="text-xs text-zinc-400">{invites.length}</span>
          </div>
          {invites.length === 0 ? (
            <div className="p-8 text-center text-sm text-zinc-400">
              No pending invites. Click <span className="font-medium">Invite team member</span> above to send one.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100" data-testid="team-invites-list">
              {invites.map((inv) => (
                <li key={inv.id} className="px-5 py-3.5 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#001E44] truncate">{inv.email}</div>
                    {inv.job_title && (
                      <div className="text-xs text-zinc-500 mt-0.5">{inv.job_title}</div>
                    )}
                  </div>
                  <span className={`chip text-[10px] ${
                    inv.team_access === "full" ? "bg-[#007BFF]/10 text-[#007BFF]"
                    : inv.team_access === "editor" ? "bg-emerald-50 text-emerald-700"
                    : "bg-zinc-100 text-zinc-600"
                  }`}>
                    {ACCESS_LABEL[inv.team_access] || "Full access"}
                  </span>
                  <span className={`chip text-[10px] ${inv.email_sent ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
                    {inv.email_sent ? "Email sent" : "Awaiting send"}
                  </span>
                  <RevokeInviteButton invite={inv} onRevoked={load} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
    </AppShell>
  );
}

function InviteTeammateDialog({ onInvited }) {
  const [form, setForm] = useState({ email: "", job_title: "", team_access: "full" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/team/contractor-invites", form);
      toast.success(`Invite sent to ${form.email}`);
      setForm({ email: "", job_title: "", team_access: "full" });
      onInvited?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid="invite-teammate-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
          Invite a team member
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Email</Label>
          <Input
            required type="email" autoComplete="off"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            data-testid="invite-email-input"
            placeholder="teammate@yourcompany.com"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Job title</Label>
          <Input
            required maxLength={120}
            value={form.job_title}
            onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
            data-testid="invite-job-title-input"
            placeholder="e.g., Site Supervisor, Foreman, Estimator"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Access level</Label>
          <Select
            value={form.team_access}
            onValueChange={(v) => setForm((f) => ({ ...f, team_access: v }))}
          >
            <SelectTrigger data-testid="invite-access-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["full", "editor", "viewer"].map((k) => (
                <SelectItem key={k} value={k}>{ACCESS_LABEL[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-zinc-500 leading-snug">
            {ACCESS_DESCRIPTION[form.team_access]}
          </p>
        </div>
        <DialogFooter className="pt-2">
          <Button
            type="submit" disabled={busy}
            className="bg-[#007BFF] hover:bg-[#0066D6] text-white rounded-full font-semibold px-5"
            data-testid="invite-submit"
          >
            {busy ? "Sending…" : "Send invite"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function EditTeammateDialog({ member, onSaved }) {
  const [form, setForm] = useState({
    job_title: member.job_title || "",
    team_access: member.team_access || "full",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/team/contractor-team/${member.id}`, form);
      toast.success(`Updated ${member.name}`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-md" data-testid={`edit-teammate-dialog-${member.id}`}>
      <DialogHeader>
        <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
          Edit {member.name}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Job title</Label>
          <Input
            value={form.job_title}
            onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
            data-testid={`edit-job-title-${member.id}`}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="label-eyebrow">Access level</Label>
          <Select
            value={form.team_access}
            onValueChange={(v) => setForm((f) => ({ ...f, team_access: v }))}
          >
            <SelectTrigger data-testid={`edit-access-${member.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {["full", "editor", "viewer"].map((k) => (
                <SelectItem key={k} value={k}>{ACCESS_LABEL[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-zinc-500 leading-snug">
            {ACCESS_DESCRIPTION[form.team_access]}
          </p>
        </div>
        <DialogFooter className="pt-2">
          <Button
            type="submit" disabled={busy}
            className="bg-[#007BFF] hover:bg-[#0066D6] text-white rounded-full font-semibold px-5"
            data-testid={`save-teammate-${member.id}`}
          >
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function RemoveTeammateButton({ member, onRemoved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/team/contractor-team/${member.id}`);
      toast.success(`Removed ${member.name}`);
      setOpen(false);
      onRemoved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className="p-1.5 rounded text-zinc-400 hover:bg-red-50 hover:text-red-700 transition-colors"
          data-testid={`remove-team-member-${member.id}`}
          title="Remove teammate"
        >
          <Trash size={14} weight="bold" />
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm" data-testid={`remove-team-member-dialog-${member.id}`}>
        <DialogHeader>
          <DialogTitle className="font-display font-bold tracking-tight text-[#001E44]">
            Remove {member.name}?
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-zinc-600">
          They&apos;ll lose access to your team&apos;s projects. Items they worked on stay where they are.
        </p>
        <DialogFooter className="pt-2 gap-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            disabled={busy}
            onClick={remove}
            className="bg-red-600 hover:bg-red-700 text-white rounded-full px-5"
            data-testid={`remove-team-member-confirm-${member.id}`}
          >
            {busy ? "Removing…" : "Remove teammate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RevokeInviteButton({ invite, onRevoked }) {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.delete(`/team/contractor-invites/${invite.id}`);
      toast.success("Invite revoked");
      onRevoked?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      onClick={submit}
      disabled={busy}
      className="p-1.5 rounded text-zinc-400 hover:bg-red-50 hover:text-red-700 transition-colors disabled:opacity-50"
      data-testid={`revoke-invite-${invite.id}`}
      title="Revoke invite"
    >
      <Trash size={14} weight="bold" />
    </button>
  );
}
